import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, UserGroupIcon, BanknotesIcon, ExclamationTriangleIcon, CheckCircleIcon, WrenchIcon, XMarkIcon, DocumentArrowDownIcon, TableCellsIcon } from '@heroicons/react/24/outline';

import { Dialog, Transition } from '@headlessui/react';
import jsPDF from 'jspdf';
import { toast } from 'react-hot-toast';
import { getPayrollImportById, type PayrollImportDocument, type PayrollAuditItem, removeAuditItem } from '../services';
import { useToast } from '@/hooks/useToast';
import { fetchCollaboratorsSalary, type CollaboratorSalaryData, type SalaryHistoryEntry } from '@/features/Collaborators/Services/salarySubcollectionService';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { collaboratorService } from '@/features/Collaborators/Services/collaboratorService';
import { useSalaryMutation } from '@/features/Collaborators/hooks/data/useSalaryMutation';
import { CurrencyInput } from '@/components/shared';

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const toTitleCase = (str: string) => {
  return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const formatCpf = (cpf: string) => {
  const digits = cpf.replace(/\D/g, '');
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

interface SalaryFixModalProps {
  isOpen: boolean;
  onClose: () => void;
  collaboratorId: string;
  collaboratorName: string;
  initialAmount: number;
  competence: string;
  onSuccess: () => void;
  initialReason?: string;
  initialExtras?: number;
}

function SalaryFixModal({ isOpen, onClose, collaboratorId, collaboratorName, initialAmount, competence, onSuccess, initialReason = '', initialExtras = 0 }: SalaryFixModalProps) {
  const { updateSalary } = useSalaryMutation(collaboratorId);
  const [amount, setAmount] = useState(initialAmount);
  const [extras, setExtras] = useState(initialExtras);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState(initialReason);
  const [observations, setObservations] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && competence) {
      const parts = competence.split('-');
      if (parts.length === 2) {
        setDate(`${parts[0]}-${parts[1]}-01`);
      } else {
        console.error('Invalid competence format:', competence);
        setDate('');
      }
      setAmount(initialAmount);
      setExtras(initialExtras);
      setReason(initialReason);
      setObservations('');
    }
  }, [isOpen, competence, initialAmount, initialReason, initialExtras]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!date) {
      toast.error('Data de competência inválida');
      return;
    }

    console.log('[SalaryFixModal] Submitting:', { collaboratorId, amount, date, reason });

    setIsSubmitting(true);
    updateSalary.mutate({
      data: {
        amount,
        extras,
        salaryType: 'CLT',
        effectiveDate: date,
      },
      reason,
      observations,
    }, {
      onSuccess: () => {
        console.log('[SalaryFixModal] Success');
        setIsSubmitting(false);
        onSuccess();
        onClose();
      },
      onError: (err) => {
        console.error('[SalaryFixModal] Error:', err);
        toast.error('Erro ao ajustar salário. Tente novamente.');
        setIsSubmitting(false);
      }
    });
  };
  const salaryReasons = [
    'Salário Inicial',
    'Reajuste após periodo de experiência',
    'Mérito',
    'Dissídio',
    'Troca de Projeto',
    'Troca de Função',
    'Outros'
  ];

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Ajustar Salário
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-4 text-sm text-gray-500">
                  Colaborador: <span className="font-medium text-gray-700">{collaboratorName}</span>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Data de Vigência</label>
                    <input
                      type="date"
                      required
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <CurrencyInput
                      id="amount"
                      name="amount"
                      label="Novo Salário"
                      value={amount}
                      onValueChange={(_val, _name, values) => setAmount(values.float)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Motivo</label>
                    <select
                      required
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="">Selecione...</option>
                      {salaryReasons.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Observações</label>
                    <textarea
                      rows={3}
                      value={observations}
                      onChange={(e) => setObservations(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Salvando...' : 'Salvar Ajuste'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

interface ConfirmFixModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  isSubmitting: boolean;
}

function ConfirmFixModal({ isOpen, onClose, onConfirm, title, message, isSubmitting }: ConfirmFixModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                  {title}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">{message}</p>
                </div>

                <div className="mt-4 flex justify-end gap-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                    onClick={onConfirm}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Confirmando...' : 'Confirmar'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export function PayrollImportDetailsPage() {
  const { importId } = useParams<{ importId: string }>();
  const navigate = useNavigate();
  const { error: showError, success: showSuccess } = useToast();
  const { batchUpdate } = useSalaryMutation('');


  const [importData, setImportData] = useState<PayrollImportDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [salaryData, setSalaryData] = useState<Record<string, CollaboratorSalaryData>>({});
  const [loadingSalaries, setLoadingSalaries] = useState(false);

  // Fix States
  const [fixingItem, setFixingItem] = useState<PayrollAuditItem | null>(null);
  const [isSalaryModalOpen, setIsSalaryModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isFixing, setIsFixing] = useState(false);

  const fetchData = async () => {
    if (!importId) return;
    try {
      setLoading(true);
      const data = await getPayrollImportById(importId);
      if (!data) {
        showError('Importação não encontrada.');
        navigate('/expenses/payroll', { replace: true });
        return;
      }
      setImportData(data);
      if (data.collaborators.length) {
        setLoadingSalaries(true);
        const ids = data.collaborators.map((c) => c.collaboratorId);
        const salaries = await fetchCollaboratorsSalary(ids);
        setSalaryData(salaries);
        setLoadingSalaries(false);
      }
    } catch (err) {
      console.error('[PayrollImportDetailsPage] Erro ao carregar import', err);
      showError('Não foi possível carregar a importação.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importId]);

  const handleFix = (item: PayrollAuditItem) => {
    setFixingItem(item);
    if (item.issueType === 'salary_mismatch' || item.issueType === 'union_adjustment_mismatch') {
      setIsSalaryModalOpen(true);
    } else {
      setIsConfirmModalOpen(true);
    }
  };

  const executeFix = async () => {
    if (!importData || !fixingItem) return;

    const matchedCollab = importData.collaborators.find(
      c => c.payrollEmployeeId === fixingItem.employeeId || c.collaboratorName === fixingItem.employeeName
    );

    if (!matchedCollab) {
      showError('Não foi possível identificar o colaborador para correção.');
      setIsConfirmModalOpen(false);
      return;
    }

    const collaboratorId = matchedCollab.collaboratorId;
    setIsFixing(true);

    try {
      if (fixingItem.issueType === 'name_mismatch') {
        const newName = toTitleCase(fixingItem.foundValue as string);
        await collaboratorService.updatePartialCollaborator({
          id: collaboratorId,
          data: { name: newName }
        });
        showSuccess('Nome atualizado com sucesso!');
      } else if (fixingItem.issueType === 'cpf_mismatch') {
        const newCpf = formatCpf(String(fixingItem.foundValue).replace(/\D/g, ''));
        await collaboratorService.updatePartialCollaborator({
          id: collaboratorId,
          data: { cpf: newCpf }
        });
        showSuccess('CPF atualizado com sucesso!');
      } else if (fixingItem.issueType === 'admission_date_mismatch') {
        const newDate = String(fixingItem.foundValue);
        await collaboratorService.updatePartialCollaborator({
          id: collaboratorId,
          data: { startDate: newDate }
        });
        showSuccess('Data de admissão atualizada com sucesso!');
      }

      // Remove from audit list
      await removeAuditItem(importData.id, { employeeId: fixingItem.employeeId, issueType: fixingItem.issueType });

      // Update local state
      setImportData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          audit: prev.audit?.filter(item => !(item.employeeId === fixingItem.employeeId && item.issueType === fixingItem.issueType))
        };
      });

      setIsConfirmModalOpen(false);
      setFixingItem(null);
    } catch (err) {
      console.error('Erro ao corrigir item:', err);
      showError('Falha ao aplicar correção.');
    } finally {
      setIsFixing(false);
    }
  };

  const handleSalarySuccess = async () => {
    if (!importData || !fixingItem) return;

    try {
      // Remove from audit list
      await removeAuditItem(importData.id, { employeeId: fixingItem.employeeId, issueType: fixingItem.issueType });

      // Update local state
      setImportData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          audit: prev.audit?.filter(item => !(item.employeeId === fixingItem.employeeId && item.issueType === fixingItem.issueType))
        };
      });

      showSuccess('Salário ajustado e pendência resolvida!');
      setFixingItem(null);
      // Optionally refresh salaries to show updated value in the list, but not full page reload
      // We can trigger a background fetch for salaries if needed, but the audit item removal is the main goal.
    } catch (err) {
      console.error('Erro ao remover pendência de salário:', err);
      showError('Salário ajustado, mas erro ao atualizar lista de pendências.');
    }
  };



  const sortedAudit = useMemo(() => {
    if (!importData?.audit) return [];
    const priority: Record<string, number> = {
      salary_mismatch: 1,
      union_adjustment_mismatch: 2,
      admission_date_mismatch: 3,
      name_mismatch: 4,
      cpf_mismatch: 5,
      unmatched: 6,
    };
    return [...importData.audit].sort((a, b) => {
      const pA = priority[a.issueType] ?? 99;
      const pB = priority[b.issueType] ?? 99;
      if (pA !== pB) return pA - pB;
      return a.employeeName.localeCompare(b.employeeName);
    });
  }, [importData?.audit]);

  const unionAdjustmentItems = useMemo(() => {
    console.log('[PayrollImportDetailsPage] sortedAudit:', sortedAudit);
    const items = sortedAudit.filter(item => item.issueType === 'union_adjustment_mismatch');
    console.log('[PayrollImportDetailsPage] unionAdjustmentItems:', items);
    return items;
  }, [sortedAudit]);

  const handleBatchFixUnion = async () => {
    if (!importData || !unionAdjustmentItems.length) return;

    if (!confirm(`Deseja aplicar o reajuste sindical para ${unionAdjustmentItems.length} colaboradores?`)) {
      return;
    }

    const parts = importData.competence?.split('-') ?? [];
    let effectiveDate = '';
    if (parts.length === 2) {
      effectiveDate = `${parts[0]}-${parts[1]}-01`;
    } else {
      showError('Competência inválida.');
      return;
    }

    setIsFixing(true);
    try {
      const updates = unionAdjustmentItems.map(item => {
        const collaborator = importData.collaborators.find(
          c => c.payrollEmployeeId === item.employeeId || c.collaboratorName === item.employeeName
        );

        if (!collaborator) {
          throw new Error(`Colaborador não encontrado: ${item.employeeName}`);
        }

        const history = salaryData[collaborator.collaboratorId]?.history || [];
        const previousExtras = getPreviousExtras(history, effectiveDate);

        return {
          collaboratorId: collaborator.collaboratorId,
          data: {
            amount: typeof item.foundValue === 'number' ? item.foundValue : 0,
            extras: previousExtras,
            salaryType: 'CLT' as const,
            effectiveDate,
          },
          reason: 'Dissídio',
          observations: '',
        };
      });

      console.log('[handleBatchFixUnion] Sending updates:', updates);
      await batchUpdate.mutateAsync(updates);

      // Remove all fixed items from audit
      for (const item of unionAdjustmentItems) {
        await removeAuditItem(importData.id, { employeeId: item.employeeId, issueType: item.issueType });
      }

      // Update local state
      setImportData(prev => {
        if (!prev) return null;
        return {
          ...prev,
          audit: prev.audit?.filter(item => !unionAdjustmentItems.some(fixed => fixed.employeeId === item.employeeId && fixed.issueType === item.issueType))
        };
      });

      showSuccess(`${unionAdjustmentItems.length} reajustes aplicados com sucesso!`);
    } catch (err) {
      console.error('Erro no reajuste em lote:', err);
      showError('Erro ao aplicar reajustes em lote.');
    } finally {
      setIsFixing(false);
    }
  };



  const handleExportPdf = () => {
    if (!sortedAudit.length) return;

    const doc = new jsPDF();

    // Header
    doc.setFontSize(18);
    doc.text('Relatório de Auditoria de Folha', 20, 20);

    doc.setFontSize(12);
    doc.text(`Competência: ${importData?.competence}`, 20, 30);
    doc.text(`Data de Geração: ${new Date().toLocaleDateString('pt-BR')}`, 20, 36);

    let y = 50;

    sortedAudit.forEach((item) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${item.employeeName} (${item.employeeId})`, 20, y);
      y += 6;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      const typeLabel = item.issueType === 'salary_mismatch'
        ? 'Salário'
        : item.issueType === 'admission_date_mismatch'
          ? 'Admissão'
          : item.issueType === 'name_mismatch'
            ? 'Nome'
            : item.issueType === 'cpf_mismatch'
              ? 'CPF'
              : item.issueType === 'union_adjustment_mismatch'
                ? 'Reajuste Sindical'
                : 'Vínculo';

      doc.text(`Tipo: ${typeLabel}`, 20, y);
      y += 5;

      const descLines = doc.splitTextToSize(`Descrição: ${item.description}`, 170);
      doc.text(descLines, 20, y);
      y += descLines.length * 4 + 2;

      const expected = item.expectedValue !== undefined
        ? typeof item.expectedValue === 'number'
          ? formatCurrency(item.expectedValue)
          : item.expectedValue
        : '—';

      const found = item.foundValue !== undefined
        ? typeof item.foundValue === 'number'
          ? formatCurrency(item.foundValue)
          : item.foundValue
        : '—';

      doc.text(`No Cadastro: ${expected} | No Arquivo: ${found}`, 20, y);
      y += 10;

      doc.setDrawColor(200);
      doc.line(20, y - 5, 190, y - 5);
      y += 5;
    });

    doc.save(`auditoria-${importData?.competence}.pdf`);
  };

  const handleExportCsv = () => {
    if (!sortedAudit.length) return;

    const headers = ['Colaborador', 'ID', 'Tipo', 'Descrição', 'No Cadastro', 'No Arquivo'];
    const rows = sortedAudit.map(item => {
      const typeLabel = item.issueType === 'salary_mismatch'
        ? 'Salário'
        : item.issueType === 'admission_date_mismatch'
          ? 'Admissão'
          : item.issueType === 'name_mismatch'
            ? 'Nome'
            : item.issueType === 'cpf_mismatch'
              ? 'CPF'
              : item.issueType === 'union_adjustment_mismatch'
                ? 'Reajuste Sindical'
                : 'Vínculo';

      const expected = item.expectedValue !== undefined
        ? typeof item.expectedValue === 'number'
          ? item.expectedValue.toFixed(2).replace('.', ',')
          : item.expectedValue
        : '';

      const found = item.foundValue !== undefined
        ? typeof item.foundValue === 'number'
          ? item.foundValue.toFixed(2).replace('.', ',')
          : item.foundValue
        : '';

      return [
        `"${item.employeeName}"`,
        `"${item.employeeId}"`,
        `"${typeLabel}"`,
        `"${item.description}"`,
        `"${expected}"`,
        `"${found}"`
      ].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `auditoria-${importData?.competence}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totals = useMemo(() => {
    if (!importData) return { earnings: 0, deductions: 0 };
    const earnings =
      typeof importData.totals?.earning?.total === 'number'
        ? importData.totals.earning.total
        : importData.collaborators.reduce((sum, col) => sum + (col.earningsTotal ?? 0), 0);
    const deductions =
      typeof importData.totals?.deduction?.total === 'number'
        ? importData.totals.deduction.total
        : importData.collaborators.reduce((sum, col) => sum + (col.deductionsTotal ?? 0), 0);
    const earningsBreakdown = Object.entries(importData.totals?.earning?.byCode ?? {})
      .map(([code, info]) => ({
        code,
        description: info?.description ?? '',
        total: info?.total ?? 0,
      }))
      .sort((a, b) => b.total - a.total);
    const deductionBreakdown = Object.entries(importData.totals?.deduction?.byCode ?? {})
      .map(([code, info]) => ({
        code,
        description: info?.description ?? '',
        total: info?.total ?? 0,
      }))
      .sort((a, b) => b.total - a.total);
    return { earnings, deductions, earningsBreakdown, deductionBreakdown };
  }, [importData]);

  const getEffectiveSalaryForCompetence = (history: SalaryHistoryEntry[], competence?: string) => {
    if (!competence) return null;
    const [yearStr, monthStr] = competence.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    if (Number.isNaN(year) || Number.isNaN(month)) return null;
    const endOfMonth = new Date(year, month, 0);
    const ordered = [...history].sort((a, b) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime());
    const entry = ordered.find((item) => new Date(item.changeDate).getTime() <= endOfMonth.getTime());
    return entry?.amount ?? null;
  };

  const getPreviousExtras = (history: SalaryHistoryEntry[], effectiveDateStr: string): number => {
    if (!history.length || !effectiveDateStr) return 0;
    const effectiveDate = new Date(effectiveDateStr);
    // Sort history descending by date
    const ordered = [...history].sort((a, b) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime());
    // Find the first entry that is strictly before the new effective date
    // OR if the new effective date is the same as an existing one, we might want the one before that?
    // Logic: We want the "current" state just before this new change takes effect.
    // If we are inserting a record at 2023-11-01, we want the record that was active on 2023-10-31.
    const targetDate = new Date(effectiveDate);
    targetDate.setDate(targetDate.getDate() - 1); // Go back one day

    const entry = ordered.find((item) => new Date(item.changeDate).getTime() <= targetDate.getTime());
    return entry?.extras ?? 0;
  };

  const fixingCollaborator = useMemo(() => {
    if (!importData || !fixingItem) return null;
    return importData.collaborators.find(
      c => c.payrollEmployeeId === fixingItem.employeeId || c.collaboratorName === fixingItem.employeeName
    );
  }, [importData, fixingItem]);

  if (loading) {
    return <div className="text-sm text-gray-600">Carregando importação...</div>;
  }

  if (!importData) {
    return <div className="text-sm text-gray-600">Importação não encontrada.</div>;
  }



  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo="/expenses/payroll"
        icon={<BanknotesIcon className="h-5 w-5" aria-hidden />}
        badge="Competência"
        title={importData.competence ?? 'Sem competência'}
        actions={
          <div className="flex flex-col items-start text-xs text-gray-500 sm:items-end">
            <span>Import ID: {importId}</span>
            {importData.createdAt ? (
              <span>
                Criado em{' '}
                {new Date(importData.createdAt).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500">Proventos</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(totals.earnings)}</div>
            </div>
            <span className="text-xs font-semibold text-gray-500">
              {totals.earningsBreakdown?.length ?? 0} códigos
            </span>
          </div>
          {totals.earningsBreakdown?.length ? (
            <div className="mt-4 overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Código</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Descrição</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {totals.earningsBreakdown.map((item) => (
                    <tr key={item.code}>
                      <td className="px-3 py-2 font-semibold text-gray-900">{item.code}</td>
                      <td className="px-3 py-2 text-gray-700">{item.description || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">
                        {formatCurrency(item.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-3 text-sm text-gray-600">Sem subtotais disponíveis.</div>
          )}
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase text-gray-500">Deduções</div>
              <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(totals.deductions)}</div>
            </div>
            <span className="text-xs font-semibold text-gray-500">
              {totals.deductionBreakdown?.length ?? 0} códigos
            </span>
          </div>
          {totals.deductionBreakdown?.length ? (
            <div className="mt-4 overflow-x-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Código</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Descrição</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {totals.deductionBreakdown.map((item) => (
                    <tr key={item.code}>
                      <td className="px-3 py-2 font-semibold text-gray-900">{item.code}</td>
                      <td className="px-3 py-2 text-gray-700">{item.description || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">
                        {formatCurrency(item.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="mt-3 text-sm text-gray-600">Sem subtotais disponíveis.</div>
          )}
        </div>
      </div>

      <div className="bg-white shadow sm:rounded-lg overflow-hidden border border-gray-200">
        <div
          className={`px-4 py-5 sm:px-6 border-b flex justify-between items-center ${(importData.audit?.length ?? 0) > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
            }`}
        >
          <div>
            <h3
              className={`text-lg leading-6 font-medium flex items-center gap-2 ${(importData.audit?.length ?? 0) > 0 ? 'text-red-800' : 'text-green-800'
                }`}
            >
              {(importData.audit?.length ?? 0) > 0 ? (
                <ExclamationTriangleIcon className="h-5 w-5" />
              ) : (
                <CheckCircleIcon className="h-5 w-5" />
              )}
              Auditoria Global ({importData.audit?.length ?? 0})
            </h3>
            <p
              className={`mt-1 max-w-2xl text-sm ${(importData.audit?.length ?? 0) > 0 ? 'text-red-600' : 'text-green-600'
                }`}
            >
              {(importData.audit?.length ?? 0) > 0
                ? 'Inconsistências encontradas na importação.'
                : 'Não foram encontradas pendências de auditoria no arquivo.'}
            </p>
          </div>
          {(importData.audit?.length ?? 0) > 0 && (
            <div className="flex items-center gap-2">
              {unionAdjustmentItems.length >= 1 && (
                <button
                  onClick={handleBatchFixUnion}
                  disabled={isFixing}
                  className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
                  title="Corrigir todos os reajustes sindicais"
                >
                  {isFixing ? (
                    'Processando...'
                  ) : (
                    <>
                      <WrenchIcon className="h-3.5 w-3.5" />
                      Corrigir Todos (Dissídio) ({unionAdjustmentItems.length})
                    </>
                  )}
                </button>
              )}
              <button
                onClick={handleExportCsv}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-black/5 transition-colors"
                title="Exportar CSV"
              >
                <TableCellsIcon className="h-6 w-6" />
              </button>
              <button
                onClick={handleExportPdf}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-black/5 transition-colors"
                title="Exportar PDF"
              >
                <DocumentArrowDownIcon className="h-6 w-6" />
              </button>
            </div>
          )}
        </div>
        {(importData.audit?.length ?? 0) > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Colaborador
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Tipo
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Descrição
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    No Cadastro
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    No Arquivo
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedAudit.map((item, idx) => {
                  const canFix = ['salary_mismatch', 'name_mismatch', 'cpf_mismatch', 'admission_date_mismatch', 'union_adjustment_mismatch'].includes(item.issueType);
                  return (
                    <tr key={idx}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.employeeName}
                        <div className="text-xs text-gray-500">{item.employeeId}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.issueType === 'salary_mismatch'
                          ? 'Salário'
                          : item.issueType === 'admission_date_mismatch'
                            ? 'Admissão'
                            : item.issueType === 'name_mismatch'
                              ? 'Nome'
                              : item.issueType === 'cpf_mismatch'
                                ? 'CPF'
                                : item.issueType === 'union_adjustment_mismatch'
                                  ? 'Reajuste Sindical'
                                  : 'Vínculo'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                        {item.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.expectedValue !== undefined
                          ? typeof item.expectedValue === 'number'
                            ? formatCurrency(item.expectedValue)
                            : item.expectedValue
                          : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.foundValue !== undefined
                          ? typeof item.foundValue === 'number'
                            ? formatCurrency(item.foundValue)
                            : item.foundValue
                          : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {canFix && (
                          <button
                            onClick={() => handleFix(item)}
                            className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                          >
                            <WrenchIcon className="h-3 w-3" />
                            Corrigir
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Colaboradores</h2>
            <p className="text-sm text-gray-600">Resumo dos colaboradores importados.</p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
            <UserGroupIcon className="h-4 w-4" aria-hidden />
            {importData.collaborators.length} registrad{importData.collaborators.length === 1 ? 'o' : 'os'}
          </div>
        </div>

        {importData.collaborators.length === 0 ? (
          <div className="mt-4 rounded-md border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            Nenhum colaborador encontrado nesta importação.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Colaborador</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Salário (folha)</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Proventos</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Deduções</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {importData.collaborators.map((col) => {
                  const salaryInfo = salaryData[col.collaboratorId];
                  const payrollSalary = col.payrollSalary ?? null;
                  const effectiveSalary = salaryInfo
                    ? getEffectiveSalaryForCompetence(salaryInfo.history, importData.competence)
                    : null;

                  return (
                    <tr key={col.collaboratorId}>
                      <td className="px-4 py-3 text-gray-900">
                        <Link
                          to={`/expenses/payroll/${importId}/collaborator/${col.collaboratorId}`}
                          className="font-semibold text-primary-700 underline underline-offset-2 hover:text-primary-800"
                        >
                          {col.collaboratorName}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className="inline-flex items-center gap-1">
                          <span>{formatCurrency(payrollSalary)}</span>
                        </span>
                        {effectiveSalary != null && (
                          <div className="text-[11px] text-gray-500">
                            Vigente: {formatCurrency(effectiveSalary)}
                          </div>
                        )}
                        {loadingSalaries && !salaryInfo && (
                          <div className="text-[11px] text-gray-400">Carregando salário...</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(col.earningsTotal)}</td>
                      <td className="px-4 py-3 text-gray-700">{formatCurrency(col.deductionsTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

          </div>
        )}
      </div>

      {fixingItem && isSalaryModalOpen && fixingCollaborator && (
        <SalaryFixModal
          key={fixingItem.employeeId}
          isOpen={isSalaryModalOpen}
          onClose={() => {
            setIsSalaryModalOpen(false);
            setFixingItem(null);
          }}
          collaboratorId={fixingCollaborator.collaboratorId}
          collaboratorName={fixingItem.employeeName}
          initialAmount={typeof fixingItem?.foundValue === 'number' ? fixingItem.foundValue : 0}
          competence={importData?.competence ?? ''}
          onSuccess={handleSalarySuccess}
          initialReason={fixingItem?.issueType === 'union_adjustment_mismatch' ? 'Dissídio' : ''}
          initialExtras={(() => {
            if (!fixingItem || !importData) return 0;
            const collab = importData.collaborators.find(
              c => c.payrollEmployeeId === fixingItem.employeeId || c.collaboratorName === fixingItem.employeeName
            );
            if (!collab) return 0;
            const history = salaryData[collab.collaboratorId]?.history || [];
            // Calculate effective date (1st of competence)
            const parts = (importData.competence ?? '').split('-');
            const effectiveDate = parts.length === 2 ? `${parts[0]}-${parts[1]}-01` : '';
            return getPreviousExtras(history, effectiveDate);
          })()}
        />
      )}

      <ConfirmFixModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setFixingItem(null);
        }}
        onConfirm={executeFix}
        title="Confirmar Correção"
        message={`Deseja realmente aplicar a correção para ${fixingItem?.employeeName}? Esta ação atualizará os dados do colaborador.`}
        isSubmitting={isFixing}
      />
    </div>
  );
}
